import React, { useState, useMemo } from 'react';
import { finalize } from 'rxjs/operators';
import { throwError } from 'rxjs';
import CardBody from 'components/Card/CardBody';
import CardFooter from 'components/Card/CardFooter';
import CustomInput from 'components/CustomInput/CustomInput';
import Button from 'components/CustomButtons/Button';
import CustomLink from 'components/CustomLink/CustomLink';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch, useSelector } from 'react-redux';
import { AuthorizationService } from 'services/authorization/authorization';
import { translate } from 'components/Translation';
import { makeStyles } from '@material-ui/core/styles';
import Email from '@material-ui/icons/EmailOutlined';
import PictureInPicture from '@material-ui/icons/PictureInPicture';
import EmailSent from 'assets/img/email-sent.svg';
import styles from 'assets/jss/material-dashboard-pro-react/views/forgotPageStyle';
import { BodyM, DisplayS, Success, SuccessBold } from 'components/Typography/Typography';
import ErrorBox from 'components/ErrorBox/ErrorBox';
import { useLocation } from 'react-router';
import { getPasswordRulesLabels } from 'redux/modules/dynamicLabels/selectors';

// @ts-ignore
const useStyles = makeStyles(styles);
interface FormData {
	email: string;
	lastFourDigits: string;
}

interface IFormProps {
	formData: FormData;
	onChange: (name: keyof FormData, value: string) => void;
	isValid: boolean;
	onSubmit: () => void;
	isResetSuccessful: boolean;
}

const useQuery = () => {
	return new URLSearchParams(useLocation().search);
};

const Form = (props: IFormProps) => {
	const classes = useStyles();
	const { formData, onChange, isValid, onSubmit, isResetSuccessful } = props;
	const query = useQuery();
	const forceUpdatePassword = query.get('forceUpdate') == 'true';
	const passwordRules = useSelector(getPasswordRulesLabels);

	return (
		<form
			onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
				event.preventDefault();
				onSubmit();
			}}
		>
			{isResetSuccessful ? (
				<React.Fragment>
					<DisplayS color="brand">{translate('forgot.form.header')}</DisplayS>
					<BodyM color="primary-light">{translate('forgot.form.body')}</BodyM>
				</React.Fragment>
			) : (
				<ErrorBox text={translate('forgot.unsuccessfulReset')} />
			)}

			<CustomInput
				labelText={translate('login.email')}
				id="email"
				inputProps={{
					required: true,
					value: formData.email,
					type: 'text',
					onChange: (event: React.FormEvent<HTMLInputElement>) =>
						onChange('email', (event.target as HTMLInputElement).value),
				}}
				formControlProps={{
					fullWidth: true,
				}}
				icon={<Email />}
			/>
			<CustomInput
				labelText={translate('forgot.last.4.digits')}
				id="last-four-digits"
				inputProps={{
					required: true,
					value: formData.lastFourDigits,
					type: 'text',
					onChange: (event: React.FormEvent<HTMLInputElement>) =>
						onChange('lastFourDigits', (event.target as HTMLInputElement).value),
				}}
				formControlProps={{
					fullWidth: true,
				}}
				icon={<PictureInPicture />}
			/>
			{forceUpdatePassword && (
				<React.Fragment>
					<BodyM color="danger">{passwordRules.forceUpdate}</BodyM>
				</React.Fragment>
			)}
			<div className={classes.buttonContainer}>
				<Button type="submit" disabled={!isValid} color="primary-light" id="reset-password">
					{translate('next')}
				</Button>
			</div>
		</form>
	);
};

interface IConfirmationPageProps {
	email: string;
}

const ConfirmationPage = (props: IConfirmationPageProps) => {
	const classes = useStyles();
	const { email } = props;
	return (
		<React.Fragment>
			<DisplayS color="brand">{translate('forgot.confirmation.header')}</DisplayS>
			<BodyM color="primary-light">{translate('forgot.confirmation.body')}</BodyM>
			<div className={classes.successBox}>
				<Success>{translate('forgot.confirmation.instructions')}</Success>
				<SuccessBold color="success" disableMargin>
					{email}
				</SuccessBold>
			</div>
			<div className={classes.iconContainer}>
				<img alt="email sent" src={EmailSent} />
			</div>
		</React.Fragment>
	);
};

enum STEPS {
	FORM = 1,
	CONFIRMATION,
}

const ForgotPage = () => {
	const [step, setStep] = useState<STEPS>(STEPS.FORM);
	const [destinationEmail, setDestinationEmail] = useState<string>('');
	const [isResetSuccessful, setIsResetSuccessful] = useState(true);
	const [formData, setFormData] = useState<FormData>({
		email: '',
		lastFourDigits: '',
	});

	const dispatch = useDispatch();

	const onSubmit = () => {
		dispatch(showLoadingIndicator());
		AuthorizationService.initializeResetPassword({
			loginOrEmail: formData.email,
			last4DocumentDigits: formData.lastFourDigits,
		})
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(
				({ destination }) => {
					setDestinationEmail(destination);
					setStep(2);
				},
				(error) => {
					// Not valid credentials
					setIsResetSuccessful(false);
					return throwError(error);
				}
			);
	};
	const onChange = (name: keyof FormData, value: string) =>
		setFormData((formDataState) => ({ ...formDataState, [name]: value }));

	const isFormValid = useMemo(() => Object.keys(formData).every((key) => !!formData[key]), [formData]);

	// @ts-ignore
	const classes = makeStyles(styles)();

	let page = (
		<Form
			formData={formData}
			onChange={onChange}
			isValid={isFormValid}
			onSubmit={onSubmit}
			isResetSuccessful={isResetSuccessful}
		/>
	);

	if (step === STEPS.CONFIRMATION) {
		page = <ConfirmationPage email={destinationEmail} />;
	}

	return (
		<div>
			<CardBody className={classes.cardBody}>{page}</CardBody>
			<CardFooter className={classes.footer}>
				<CustomLink to="/auth/login-page" color="brand" type="bodyMBold">
					{translate('forgot.backLogin')}
				</CustomLink>
			</CardFooter>
		</div>
	);
};

export default ForgotPage;
